import type { Option } from "@tento-lona/sheets";

/**
 * The key each rendered cell carries into the onBind reconcile.
 *
 * The reconcile diffs cells against the target rows by wire string (`rid` ↔
 * `row.id.toString()`). An ephemeral→server promote re-keys a row IN PLACE:
 * the same `RowLocalId` object, a new `toString()`. The promote skips the
 * whole-sheet rebind on purpose (so the focused editor keeps its caret), so
 * the cells still carry the OLD wire string, and the next column bind — the
 * one a plain keystroke triggers — read them as a deleted row plus an
 * inserted one: it replaced the cell, and the focused ProseMirror editor went
 * with it (core-render-trusted-focus-cud, c5).
 *
 * A cell whose `rid` matches no target row but whose recorded `serial`
 * (placement identity, survives the re-key) matches one is the same row under
 * its new name, so it takes that row's current wire string. Everything else
 * keeps its `rid` verbatim: a live match, a fresh cell (`null`), a recycled
 * cell (`onRecycle` drops both attributes), a row that really left.
 */
export function reconcileRowKeys(
  cells: readonly { rid: Option<string>; serial: Option<string> }[],
  rows: readonly { wire: string; serial: string }[],
): Option<string>[] {
  const wires = new Set<string>();
  const wireByStableId = new Map<string, string>();
  for (const row of rows) {
    wires.add(row.wire);
    wireByStableId.set(row.serial, row.wire);
  }
  return cells.map(({ rid, serial }) => {
    if (rid == null || wires.has(rid) || serial == null) return rid;
    return wireByStableId.get(serial) ?? rid;
  });
}
